import InfoOutline from "@mui/icons-material/InfoOutline";
import { Alert, Box, Grid } from "@mui/material";
import { type FC, type ReactNode, type RefObject, useMemo, useRef } from "react";
import { FormProvider, useFormContext } from "react-hook-form";
import { useFlag } from "../../server/optimizely";
import { useHandleMomentLexicalError } from "../generic/hooks/use-handle-moment-lexical-error";
import { useSetLexicalDesign } from "../generic/hooks/use-set-lexical-design";
import type { ToolbarSettings } from "../lexical-email-moment/hooks/use-email-toolbar-settings";
import { useFilterTags } from "../merge-tag/components/use-filter-tags";
import type { Tags } from "../merge-tag/types";
import { DefaultBodyPlugins } from "../message/editor/default-body-plugins";
import { DefaultContentEditable } from "../message/editor/default-content-editable";
import { DefaultPlaceholder } from "../message/editor/default-placeholder";
import { useLexicalContext } from "../message/editor/lexical-context";
import { LexicalDynamicContentContextProvider } from "../message/editor/lexical-dynamic-content-context";
import { LexicalEditorWrapper } from "../message/editor/lexical-editor-wrapper";
import { DynamicContentPlugin } from "../message/editor/plugins/dynamic-content-plugin/dynamic-content-plugin";
import { FixSmsSurveyLinkPlugin } from "../message/editor/plugins/fix-sms-survey-link-plugin";
import { GenericToolbar } from "../message/editor/plugins/generic-toolbar/generic-toolbar";
import { PollPluginMomentFormWrapper } from "../message/editor/plugins/poll-plugin/poll-plugin-moment-form-wrapper";
import type { SerialisedEditorDesign } from "../message/editor/types/serialised-editor-design";
import type { MomentFormMoment } from "../moment/types/moment-form-moment";
import { useToTarget } from "../to/use-to-target";

interface TextMomentFormProps {
  disabled?: boolean;
  showValidationErrors: boolean;
  mergeTags: Tags;
}

export const LexicalTextMomentForm: FC<TextMomentFormProps> = ({
  disabled,
  showValidationErrors,
  mergeTags,
}) => {
  const methods = useFormContext<MomentFormMoment>();
  const {
    formState: { touchedFields, errors },
  } = methods;

  return (
    <FormProvider {...methods}>
      <Grid size={{ xs: 12 }} sx={{ pt: 2 }}>
        <LexicalTextMomentFormInner
          disabled={disabled}
          showValidationErrors={showValidationErrors}
          mergeTags={mergeTags}
          design={methods.getValues("design") ?? undefined}
          error={touchedFields?.design ? errors.design?.message : undefined}
        />
      </Grid>
    </FormProvider>
  );
};

const LexicalTextMomentFormInner: FC<
  TextMomentFormProps & {
    design?: SerialisedEditorDesign;
    error?: string;
  }
> = ({ disabled, showValidationErrors, mergeTags, design, error }) => {
  const onChange = useSetLexicalDesign();
  const ref = useRef<HTMLDivElement>(null);
  const toTarget = useToTarget();
  const [smsEditorRedesignEnabled] = useFlag("cha-4420-redesign-sms-editor");
  const [addLinkEnabled] = useFlag("cha-4421-sms-add-link-button");

  const toolbarSettings: ToolbarSettings = useMemo(
    () => ({
      topLevelElements: [
        { type: "mergeTagPicker" },
        { type: "emojiPicker" },
        { type: "surveyButton", channel: "twilio" } as const,
        ...(addLinkEnabled ? [{ type: "linkButton" } as const] : []),
      ],
      floatingElements: {
        surveyLink: [{ type: "linkButton" } as const],
        text: [{ type: "coPilot" as const, channel: "twilio" as const, format: "sms" as const }],
      },
      rightAlignedElements: [{ type: "characterCount" }],
      channel: "text" as const,
    }),
    [addLinkEnabled],
  );

  const onError = useHandleMomentLexicalError();

  const filterTags = useFilterTags(
    mergeTags.filter((x) => x.version === 2),
    toTarget,
  );

  return (
    <>
      <Box sx={{ mb: 1 }}>
        <Alert
          icon={<InfoOutline />}
          color="info"
          sx={{ alignItems: "center", "& .MuiAlert-action": { pt: 0 } }}
        >
          SMS works best when the message is concise - check your character count below
        </Alert>
      </Box>
      <LexicalDynamicContentContextProvider>
        <LexicalEditorWrapper
          channelName="twilio"
          disabled={disabled}
          onError={(...args) => void onError(...args).catch(console.error)}
          error={showValidationErrors ? error : undefined}
          filterTags={filterTags}
          format="sms"
          design={design}
          renderLayout={(editor) => (
            <SmsEditorLayout redesigned={smsEditorRedesignEnabled} editorScrollRef={ref}>
              {editor}
            </SmsEditorLayout>
          )}
          slots={{
            contentEditable: ({ focusEditor, onRef }) => (
              <DefaultContentEditable
                focusEditor={focusEditor}
                onRef={onRef}
                channel="twilio"
                disabled={disabled}
                scrollerRef={smsEditorRedesignEnabled ? ref : undefined}
                scrollerProps={
                  smsEditorRedesignEnabled
                    ? {
                        sx: {
                          height: "100%",
                          minHeight: 0,
                          overflowY: "auto",
                          resize: "none",
                        },
                      }
                    : undefined
                }
              />
            ),
            placeholder: <DefaultPlaceholder />,
            plugins: {
              head: () => <TextHeadPlugins toolbarSettings={toolbarSettings} disabled={disabled} />,
              body: (historyState) => (
                <>
                  <DefaultBodyPlugins
                    auto_focus={false}
                    channel="twilio"
                    format="sms"
                    disabled={disabled}
                    onChange={onChange}
                    editorScrollRef={ref}
                    toolbarSettings={toolbarSettings}
                    historyState={historyState}
                    v1Tags={mergeTags.filter((x) => x.version === 1)}
                  />
                  <FixSmsSurveyLinkPlugin />
                  <PollPluginMomentFormWrapper />
                  <DynamicContentPlugin error={"Dynamic content is not available for SMS"} />
                </>
              ),
            },
          }}
        />
      </LexicalDynamicContentContextProvider>
    </>
  );
};

export const SmsEditorLayout: FC<{
  children: ReactNode;
  redesigned: boolean;
  editorScrollRef: RefObject<HTMLDivElement | null>;
}> = ({ children, redesigned, editorScrollRef }) => {
  const surveyLinkStyles = {
    // Override the default anchor link styles for Twilio's lexical survey links.
    "& .editor-content.twilio a.survey-link": {
      backgroundColor: "#E2FF53",
      p: 0,
      borderRadius: 0,
      color: "black",
      fontWeight: "normal",
      textDecoration: "none",
      fontSize: "inherit",
    },
  };

  if (!redesigned) {
    return (
      <Box data-testid="sms-editor-layout" sx={surveyLinkStyles} ref={editorScrollRef}>
        {children}
      </Box>
    );
  }

  return (
    <Box
      data-testid="sms-editor-layout"
      sx={{
        ...surveyLinkStyles,
        display: "flex",
        justifyContent: "center",
        backgroundColor: "background.default",
        p: 2,
      }}
    >
      <Box
        data-testid="sms-message-editor"
        sx={{
          width: 295,
          maxWidth: "100%",
          height: 350,
          overflow: "hidden",
          backgroundColor: "background.paper",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          "& .editor-inner": {
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
            resize: "none",
          },
          "& .editor-content.twilio": {
            boxSizing: "border-box",
            padding: 1,
          },
          "& [data-testid='editor-placeholder-text']": {
            left: 8,
          },
          "&:focus-within [data-testid='editor-placeholder-text']": {
            visibility: "hidden",
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const TextHeadPlugins: FC<{
  toolbarSettings: ToolbarSettings;
  disabled?: boolean;
}> = ({ toolbarSettings, disabled }) => {
  // This will eventually be rendered by the LexicalEditor calling the slot, so we can
  // use the context here
  const {
    editorModal: { handleOpen },
  } = useLexicalContext();

  return (
    <GenericToolbar
      channel={toolbarSettings.channel}
      rightAlignedElements={toolbarSettings.rightAlignedElements}
      topLevelElements={toolbarSettings.topLevelElements}
      disabled={!!disabled}
      openModal={handleOpen}
    />
  );
};
